import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Divider } from '@mui/material';
import { Link } from 'react-router-dom';
import { hasCoordinates, nearestStories, formatDistance } from '../utils/nearbyStories';

export default function NearbyStories({ incident, supabase }) {
  const [state, setState] = useState({ loading: true, stories: [], error: false });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, stories: [], error: false });
    const load = async () => {
      if (!hasCoordinates(incident)) {
        setState({ loading: false, stories: [], error: false });
        return;
      }
      try {
        if (!supabase) throw new Error('No data connection');
        let rows = [], offset = 0;
        // Read all accessible pages before ranking; the nearest story may be on a later page.
        while (!cancelled) {
          const { data, error } = await supabase.from('submissions')
            .select('id,here_happened,description,lat,lng').order('id', { ascending: true }).range(offset, offset + 499);
          if (error) throw error;
          if (!data?.length) break;
          rows.push(...data);
          offset += data.length;
        }
        if (!cancelled) setState({ loading: false, stories: nearestStories(incident, rows), error: false });
      } catch (error) {
        if (!cancelled) setState({ loading: false, stories: [], error: true });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [incident, supabase, retry]);

  return <Box component="section" aria-labelledby="nearby-title" sx={{ mt: 5, mb: 6 }}>
    <Divider sx={{ mb: 3 }} />
    <Typography id="nearby-title" component="h2" sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 1 }}>附近的气球</Typography>
    <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>按与当前气球的直线距离，由近到远排列</Typography>
    {state.loading ? <Typography role="status">正在寻找附近的气球…</Typography> : state.error ? <Box><Typography>暂时无法加载附近的气球。</Typography><Button onClick={() => setRetry(value => value + 1)}>重试</Button></Box> : !state.stories.length ? <Typography>{hasCoordinates(incident) ? '暂时没有其他带位置的气球。' : '这个气球没有位置信息，暂时无法显示附近气球。'}</Typography> :
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {state.stories.map(story => <Box key={story.id} component={Link} to={`/incident?id=${encodeURIComponent(story.id)}`} onClick={() => window.scrollTo(0, 0)} sx={{ display: 'flex', gap: 1.5, p: 2, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.25)', textDecoration: 'none', color: '#000', minWidth: 0, '&:hover': { backgroundColor: 'rgba(255,255,255,0.45)' }, '&:focus-visible': { outline: '2px solid #a50030' } }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 'bold', overflowWrap: 'anywhere' }}>{story.here_happened || '未命名的故事'}</Typography>
            <Typography sx={{ fontSize: '0.8rem', my: 0.5 }}>距当前气球约 {formatDistance(story.distanceKm)}</Typography>
            <Typography sx={{ fontSize: '0.875rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{story.description}</Typography>
          </Box>
        </Box>)}
      </Box>}
  </Box>;
}
